import { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import clubSupportAPI from "../../../services/clubSupportApi";
import { useHubBasePath } from "../../../utils/hubPaths";
import { personDisplayName, trainerPersonalChatTitle } from "../../../utils/clientAppUtils";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { useAdminAppMode } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { ChatAvatar, ReadTicks } from "./ChatThread";
import cs from "./chat.module.css";
import md from "./manageDesktop.module.css";

function chatInboxDisplayName(name) {
  if (!name) return name;
  return personDisplayName(name) || name;
}

function formatPreviewTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function InboxRow({
  to,
  state,
  photoUrl,
  name,
  preview,
  time,
  unread,
  club,
  group,
  navLink = false,
  lastIsMine = false,
  lastIsRead = false,
}) {
  const body = (
    <>
      <ChatAvatar
        photoUrl={photoUrl}
        name={name}
        club={club}
        group={group}
        className={club ? cs.chatInboxAvatarClub : cs.chatInboxAvatar}
        imgClassName={cs.chatInboxAvatarImg}
      />
      <span className={cs.chatInboxBody}>
        <span className={cs.chatInboxTop}>
          <span className={cs.chatInboxName}>{name}</span>
          <span className={cs.chatInboxTime}>{formatPreviewTime(time)}</span>
        </span>
        <span className={cs.chatInboxPreviewRow}>
          {lastIsMine ? (
            <ReadTicks read={Boolean(lastIsRead)} />
          ) : null}
          <span className={cs.chatInboxPreview}>
            {preview || "Нет сообщений"}
          </span>
        </span>
      </span>
      {unread > 0 ? (
        <span className={cs.chatInboxBadge}>
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </>
  );

  if (navLink) {
    return (
      <li>
        <NavLink
          to={to}
          state={state}
          className={({ isActive }) =>
            isActive
              ? `${cs.chatInboxRow} ${cs.chatInboxRowActive}`
              : cs.chatInboxRow
          }
        >
          {body}
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <Link to={to} state={state} className={cs.chatInboxRow}>
        {body}
      </Link>
    </li>
  );
}

function sortThreadsByActivity(threads) {
  return [...threads].sort((a, b) => {
    const ta = a.last_at ? new Date(a.last_at).getTime() : 0;
    const tb = b.last_at ? new Date(b.last_at).getTime() : 0;
    return tb - ta;
  });
}

export default function ChatInboxHub({ portal = "staff", embedded = false }) {
  const hubBase = useHubBasePath();
  const clientBase = "/app/chat";
  const { tenant } = useSelector((state) => state.auth);
  const { athleteQueryParam } = useClientAthlete();
  const [threads, setThreads] = useState([]);
  const [supportUnread, setSupportUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isManageDesktop = portal === "staff" && isAdminApp && !isMobile;
  const isSidebar = embedded || isManageDesktop;
  const useNavLinks = embedded;

  const athleteId = athleteQueryParam?.athlete_id ?? "";

  const load = useCallback(async () => {
    try {
      const supportPromise = clubSupportAPI.getSupportUnread().catch(() => ({ unread: 0 }));
      if (portal === "client") {
        const athleteParams = athleteId ? { athlete_id: athleteId } : {};
        const [data, supportData] = await Promise.all([
          clinicAPI.getClientChatInbox(athleteParams),
          supportPromise,
        ]);
        const rawThreads = (data.threads || []).map((t) => {
          if (t.thread_type !== "direct") return t;
          return {
            ...t,
            client_name: t.client_name || "Команда клуба",
          };
        });
        setThreads(sortThreadsByActivity(rawThreads));
        setSupportUnread(supportData.unread || 0);
      } else {
        const [data, supportData] = await Promise.all([
          clinicAPI.getStaffChatInbox(),
          supportPromise,
        ]);
        setThreads(sortThreadsByActivity(data.threads || []));
        setSupportUnread(supportData.unread || 0);
      }
    } catch {
      setThreads([]);
      setSupportUnread(0);
    } finally {
      setLoading(false);
    }
  }, [portal, athleteId]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 2000);
    const onActivity = () => load();
    window.addEventListener("baysport:chat-activity", onActivity);
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:chat-activity", onActivity);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [load]);

  const title = portal === "client" ? "Сообщения" : "Чат";

  const rowProps = useNavLinks ? { navLink: true } : {};

  const supportTo =
    portal === "client" ? `${clientBase}/support` : `${hubBase}/chat/support`;

  const supportRow = (
    <InboxRow
      key="platform-support"
      to={supportTo}
      name="Поддержка Бай.Спорт"
      preview="Вопросы по работе платформы и клуба"
      unread={supportUnread}
      club
      {...rowProps}
    />
  );

  const listBody = loading ? (
        <div className={cs.chatInboxLoading}>
          <div className={cs.chatLoadingDots}>
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : threads.length === 0 ? (
        <ul className={`${cs.chatInboxList} chatInboxList`}>
          {supportRow}
        </ul>
      ) : (
        <ul className={`${cs.chatInboxList} chatInboxList`}>
          {supportRow}
          {threads.map((thread) => {
            const readProps = {
              lastIsMine: Boolean(thread.last_is_mine),
              lastIsRead: Boolean(thread.last_is_read),
            };

            if (thread.thread_type === "club") {
              const clubTo =
                portal === "client" ? `${clientBase}/club` : `${hubBase}/chat/club`;
              return (
                <InboxRow
                  key="club"
                  to={clubTo}
                  club
                  name={thread.client_name || "Чат клуба"}
                  preview={thread.last_body}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...readProps}
                  {...rowProps}
                />
              );
            }

            if (thread.thread_type === "group") {
              const groupTo =
                portal === "client"
                  ? `${clientBase}/group/${thread.group_id}`
                  : `${hubBase}/chat/group/${thread.group_id}`;
              return (
                <InboxRow
                  key={thread.group_id}
                  to={groupTo}
                  state={{ name: thread.client_name }}
                  group
                  name={thread.client_name}
                  preview={thread.last_body || "Чат тренировочной группы"}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...readProps}
                  {...rowProps}
                />
              );
            }

            if (portal === "client" && thread.thread_type === "member") {
              const isStaffPeer = ["owner", "admin", "trainer"].includes(
                thread.peer_role,
              );
              const displayName = isStaffPeer
                ? trainerPersonalChatTitle(thread.client_name)
                : chatInboxDisplayName(thread.client_name);
              return (
                <InboxRow
                  key={thread.peer_id}
                  to={`${clientBase}/member/${thread.peer_id}`}
                  state={{ name: thread.client_name }}
                  photoUrl={thread.client_photo_url}
                  name={displayName}
                  preview={thread.last_body || "Личные сообщения"}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...readProps}
                  {...rowProps}
                />
              );
            }

            if (portal === "client" && thread.thread_type === "direct") {
              return (
                <InboxRow
                  key="direct"
                  to={`${clientBase}/personal`}
                  name={thread.client_name || "Команда клуба"}
                  preview={
                    thread.last_body || "Сообщения команде клуба"
                  }
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...readProps}
                  {...rowProps}
                />
              );
            }

            if (portal === "staff" && thread.thread_type === "member") {
              return (
                <InboxRow
                  key={`member-${thread.peer_id}`}
                  to={`${hubBase}/chat/member/${thread.peer_id}`}
                  state={{
                    name: chatInboxDisplayName(thread.client_name),
                  }}
                  photoUrl={thread.client_photo_url}
                  name={chatInboxDisplayName(thread.client_name)}
                  preview={thread.last_body || "Личные сообщения"}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...readProps}
                  {...rowProps}
                />
              );
            }

            return (
              <InboxRow
                key={thread.client_id || `direct-${thread.client_name}`}
                to={
                  useNavLinks
                    ? `${hubBase}/chat/client/${thread.client_id}`
                    : `${hubBase}/clients/${thread.client_id}/chat`
                }
                state={{
                  fromChatHub: true,
                  clientName: chatInboxDisplayName(thread.client_name),
                }}
                photoUrl={thread.client_photo_url}
                name={chatInboxDisplayName(thread.client_name)}
                preview={thread.last_body}
                time={thread.last_at}
                unread={thread.unread_count}
                {...readProps}
                {...rowProps}
              />
            );
          })}
        </ul>
      );

  if (isSidebar) {
    return (
      <div className={`${cs.chatHubPage} ${md.desktopChatSidebarInner}`}>
        {embedded ? (
          <header className={md.desktopChatSidebarHead}>
            <h2 className={md.desktopChatSidebarTitle}>{title}</h2>
          </header>
        ) : null}
        {listBody}
      </div>
    );
  }

  return (
    <div className={`${cs.chatHubPage} chatHubPage`}>
      <header className={cs.chatHubHeader}>
        <h1 className={cs.chatHubTitle}>{title}</h1>
        {portal === "client" && tenant?.name ? (
          <p className={cs.chatHubSub}>{tenant.name}</p>
        ) : null}
      </header>
      {listBody}
    </div>
  );
}
